import { registerBrick, GlobalProperties, VisualBrick } from 'olympe';
import { useProperty, ReactBrick, cssToSxProps, ifNotTransparent, ifNotNull } from '@olympeio/core';
import { combineLatest, debounceTime } from 'rxjs';
import React, {useRef, useEffect} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ReactDOM from "react-dom";

export default class Renderer extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return combineLatest($.observe('Renderer', false), $.observe('Hidden')).pipe(debounceTime(0));
    }

    /**
     * @override
     * @param {!Element} parent
     * @param {!Element} element
     * @return {function()}
     */
    updateParent(parent, element) {
        parent.style.overflow = 'visible';
        ReactDOM.render(element, parent);
        return () => ReactDOM.unmountComponentAtNode(parent);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [renderer, hidden] = props.values;
            const [borderColor, borderRadius, borderWidth, cssProperty, defaultColor] = [
                'Border Color', 'Border Radius', 'Border Width', 'CSS Property', 'Default Color'
            ].map((p) => useProperty($, p));

            // No renderer -> show a placeholder in DRAW
            if (!hidden && !renderer && $.get(GlobalProperties.EDITION, true)) {
                return <Box sx={{backgroundColor: 'lightgrey', width: 1, height: 1, overflow: 'hidden'}}>
                    <Typography sx={{color: 'black', padding: 1}}>
                        <b>Renderer Component</b><br/>
                        Specify <code>Renderer</code> property to use the component.
                    </Typography>
                </Box>;
            }

            const rendererContainer = useRef();

            // Create and destroy runner using useEffect
            useEffect(() => {
                // Ensure that the box element is available and that the context is not destroyed to execute the rendering.
                if (rendererContainer.current && !hidden && renderer instanceof VisualBrick && !$.isDestroyed()) {
                    const renderer$ = $.runner(renderer).setParentElement(rendererContainer.current);
                    Renderer.getPropertiesToRepeat().forEach(prop => {
                        renderer$.repeat(prop, $.observe(prop));
                    });
                    Renderer.getEventsToForward().forEach(event => {
                        renderer$.observe(event).subscribe($.trigger.bind(event));
                    });

                    // Ensure that the renderer is destroyed when the component is unmounted.
                    return () => renderer$.destroy();
                }
            }, [rendererContainer]);

            // Execute runner
            return !hidden && <Box
                ref={rendererContainer}
                sx={{
                    width: 1,
                    height: 1,
                    boxSizing: 'border-box',
                    position: 'absolute',
                    overflow: useProperty($, 'Allow Content Overflow') ? 'visible' : 'hidden',
                    ...ifNotNull('borderWidth', useProperty($, 'Border Width')),
                    ...ifNotTransparent('backgroundColor', defaultColor),
                    ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                    ...ifNotTransparent('borderColor', borderColor),
                    ...ifNotTransparent('borderStyle', 'solid', borderColor),
                    ...ifNotNull('boxSizing', 'border-box', borderWidth),
                    ...cssToSxProps(useProperty($, cssProperty))
                }}
            />;
        };
    }

    /**
     * @protected
     * @return {!Array<string>}
     */
    static getPropertiesToRepeat() {
        return [
            'Allow Content Overflow',
            'Height',
            'Tab Index',
            'Width',
            'Z-Index'
        ];
    }

    /**
     * @protected
     * @return {!Array<string>}
     */
    static getEventsToForward() {
        return ['On Click'];
    }
}

registerBrick('0188245dc1cff09a2cba', Renderer);
